import clsx from 'clsx'
import { useState } from 'react'
import React from 'react'
import { Control, Controller } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'

import c from '../styles.module.scss'
import s from './styles.module.scss'

import SelectProposalMenuNestedItems from './select-proposal-menu-nested-items/SelectProposalMenuNestedItems'
import {
  getMenuItemMaxQuantity,
  getMenuItemName,
  getMenuItemPrice,
} from './utils'
import { ISelectProposalForm } from '../../modal-select-proposal.types'
import {
  ArrivalEnum,
  ClientPricingItemTypeEnum,
  ClientPricingMenuOutputDto,
  ClientPricingMenuTypeEnum,
  DepartureEnum,
} from '../../../../../lib/graphql'
import {
  useCurrencyAreaContext,
  usePriceContext,
  useUserTypeContext,
} from '../../../../contexts'
import { NumberInputRange } from '../../../../forms'

const SelectProposalMenuItem = ({
  menu,
  control,
  formValues,
  handleQuantityError,
}: {
  menu: ClientPricingMenuOutputDto
  control: Control<ISelectProposalForm, any>
  formValues: ISelectProposalForm
  handleQuantityError?: (key: string, message: string | null) => void
}) => {
  const intl = useIntl()
  const { vatStatus } = usePriceContext()
  const { currency } = useCurrencyAreaContext()
  const { isAdmin } = useUserTypeContext()
  const [showTip, setShowTip] = useState<boolean>(true)

  const getMenuSubTitle = (
    menu: ClientPricingMenuOutputDto,
  ): React.ReactElement => {
    if (menu.type === ClientPricingMenuTypeEnum.EVENING_MENU) {
      return (
        <div className={c.scheduleContainer}>
          <FormattedMessage
            id='ui.SelectProposalMenuItem.participants'
            defaultMessage='{adults} participants'
            values={{ adults: formValues.adults }}
          />
        </div>
      )
    }

    if (
      menu.type === ClientPricingMenuTypeEnum.WORKSPACE_MENU ||
      menu.type === ClientPricingMenuTypeEnum.DAY_MENU
    ) {
      return <></>
    }

    const getArrivalMessage = (arrival?: ArrivalEnum) => {
      switch (arrival) {
        case ArrivalEnum.MORNING:
          return (
            <FormattedMessage
              id='ui.SelectProposalMenuItem.morningArrival'
              defaultMessage='Arrivée matin'
            />
          )
        case ArrivalEnum.BEFORE_LUNCH:
          return (
            <FormattedMessage
              id='ui.SelectProposalMenuItem.beforeLunchArrival'
              defaultMessage='Arrivée avant le déjeuner'
            />
          )
        case ArrivalEnum.EVENING:
          return (
            <FormattedMessage
              id='ui.SelectProposalMenuItem.eveningArrival'
              defaultMessage='Arrivée soir'
            />
          )
        default:
          return null
      }
    }

    const getDepartureMessage = (departure?: DepartureEnum) => {
      switch (departure) {
        case DepartureEnum.MORNING:
          return (
            <FormattedMessage
              id='ui.SelectProposalMenuItem.morningDeparture'
              defaultMessage='Départ matin'
            />
          )
        case DepartureEnum.AFTER_LUNCH:
          return (
            <FormattedMessage
              id='ui.SelectProposalMenuItem.afterLunchDeparture'
              defaultMessage='Départ après le déjeuner'
            />
          )
        case DepartureEnum.EVENING:
          return (
            <FormattedMessage
              id='ui.SelectProposalMenuItem.eveningDeparture'
              defaultMessage='Départ soir'
            />
          )
        default:
          return null
      }
    }

    return (
      <div className={c.scheduleContainer}>
        <div className={s.schedule}>
          {getArrivalMessage(formValues.arrival)}
        </div>
        <div className={s.schedule}>
          {getDepartureMessage(formValues.departure)}
        </div>
      </div>
    )
  }

  const getMenuQuantityTips = ({
    menu,
    intl,
  }: {
    menu: ClientPricingMenuOutputDto
    intl: IntlShape
  }): string | undefined => {
    if (menu.type === ClientPricingMenuTypeEnum.HOUSE_MENU) {
      return intl.formatMessage({
        id: 'ui.getMenuQuantityTips.dryHireRequired',
        defaultMessage:
          'La location sèche est obligatoire pour les lieux en gestion libre',
      })
    }

    if (menu.type === ClientPricingMenuTypeEnum.EVENING_MENU) {
      return intl.formatMessage({
        id: 'ui.getMenuQuantityTips.selectEveningNumber',
        defaultMessage: 'Sélectionnez le nombre de soirée',
      })
    }

    return intl.formatMessage({
      id: 'ui.getMenuQuantityTips.preSelectedBasedOnSearch',
      defaultMessage: 'Présélectionné en fonction de votre recherche',
    })
  }

  const hasSharedBedroomItem = menu.nestedItems.some(
    (item) => item.type === ClientPricingItemTypeEnum.SHARED_BEDROOMS,
  )

  return (
    <div className={clsx(c.menuItem, c.menuItemList)}>
      <div className={c.menuItemIntro}>
        <div className={c.menuItemTitle}>{getMenuItemName({ menu, intl })}</div>
        {getMenuSubTitle(menu)}
      </div>
      <div className={c.menuItemElements}>
        <SelectProposalMenuNestedItems
          nestedItems={menu.nestedItems.sort((a, b) =>
            a.label.localeCompare(b.label),
          )}
        />
      </div>
      <div className={c.menuItemPrice}>
        {getMenuItemPrice({ menu, vatStatus, currency, intl })}
      </div>
      <div className={c.menuItemQuantity}>
        <Controller
          control={control}
          name={`menus.${menu._id}.quantity`}
          render={({ field }) => {
            return (
              <NumberInputRange
                value={field.value}
                disabled={
                  menu.type === ClientPricingMenuTypeEnum.HOUSE_MENU ||
                  menu.type === ClientPricingMenuTypeEnum.WORKSPACE_MENU
                }
                onChange={(value) => {
                  setShowTip(false)
                  field.onChange(value)
                }}
                isPairOnly={hasSharedBedroomItem && !isAdmin}
                handleErrorFromParent={
                  handleQuantityError && {
                    key: `menus.${menu._id}.quantity`,
                    setError: handleQuantityError,
                  }
                }
                max={getMenuItemMaxQuantity({ menu, formValues })}
              />
            )
          }}
        />
        {showTip && (
          <div className={s.tip}>{getMenuQuantityTips({ menu, intl })}</div>
        )}
      </div>
    </div>
  )
}

export default SelectProposalMenuItem
